import { useEffect, useState } from "react";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { ArrowLeftRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Alerts() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/alerts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const request = async (a, src) => {
    const qty = Math.max(a.missing, 1);
    try {
      await api.post("/transfers/request", { from_warehouse_id: src.warehouse_id, to_warehouse_id: a.warehouse_id, note: "Réapprovisionnement (alerte stock)", lines: [{ fabric_id: a.fabric.id, quantity: Math.min(qty, src.quantity) }] });
      toast.success(`Demande envoyée à ${src.warehouse_name}`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const groups = (items || []).reduce((acc, a) => { (acc[a.warehouse_name] = acc[a.warehouse_name] || []).push(a); return acc; }, {});

  return (
    <div data-testid="alerts-page">
      <PageHeader title="Alertes stock" subtitle={`${new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · tissus sous leur seuil d'alerte`}>
        <Button variant="outline" onClick={load} data-testid="alerts-refresh"><RefreshCw size={16} /> Actualiser</Button>
      </PageHeader>
      {items && items.length === 0 && <div className="rise rounded-lg border bg-card p-8 text-center text-muted-foreground" data-testid="alerts-empty">Aucune alerte : tous les stocks sont au-dessus du seuil.</div>}
      <div className="space-y-6">
        {Object.entries(groups).map(([wh, list]) => (
          <div key={wh} className="rise overflow-hidden rounded-lg border bg-card" data-testid={`alerts-group-${wh}`}>
            <div className="flex items-center justify-between border-b bg-muted/50 px-4 py-3"><h3 className="font-display text-lg font-semibold">{wh}</h3><Badge className="bg-[#059669]">{list.length} alerte(s)</Badge></div>
            <Table>
              <TableHeader><TableRow><TableHead>Tissu</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">Seuil</TableHead><TableHead className="text-right">Manque</TableHead><TableHead>Réapprovisionner depuis</TableHead></TableRow></TableHeader>
              <TableBody>
                {list.map((a) => (
                  <TableRow key={a.fabric.id} data-testid={`alert-row-${a.fabric.reference}`}>
                    <TableCell><p className="font-semibold">{a.fabric.name}</p><p className="font-mono text-xs text-muted-foreground">{a.fabric.reference}</p></TableCell>
                    <TableCell className="text-right">{a.quantity <= 0 ? <Badge variant="destructive">Rupture</Badge> : <span className="font-semibold text-[#059669]">{fmt(a.quantity)} m</span>}</TableCell>
                    <TableCell className="text-right">{fmt(a.fabric.min_stock)} m</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(a.missing)} m</TableCell>
                    <TableCell>
                      {a.pending_qty > 0 && <Badge variant="secondary" className="mr-2">{fmt(a.pending_qty)} m en cours</Badge>}
                      {a.sources.length === 0 ? <span className="text-xs text-muted-foreground">Aucun autre dépôt n'a ce tissu</span> : (
                        <div className="flex flex-wrap gap-1">
                          {a.sources.slice(0, 3).map((s) => <Button key={s.warehouse_id} size="sm" variant="outline" onClick={() => request(a, s)} data-testid={`alert-request-${a.fabric.reference}-${s.warehouse_name}`}><ArrowLeftRight size={14} /> {s.warehouse_name} ({fmt(s.quantity)} m)</Button>)}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>
    </div>
  );
}
